import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { API } from "@/lib/api";

const CONTESTI = [
  "Scuola di musica privata",
  "Scuola secondaria a indirizzo musicale",
  "Conservatorio - corso di base o propedeutico",
  "Lezioni private",
  "Altro",
];

const empty = {
  nome: "", email: "", telefono: "", data_nascita: "", citta: "",
  famiglia: "", strumento: "", anni_studio: "", contesto: "", note: "", consenso: false,
};

export default function RegistrationForm({ families }) {
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consenso) { setError("Devi accettare il trattamento dei dati personali."); return; }
    setStatus("loading");
    try {
      await axios.post(`${API}/registrations`, form);
      setStatus("success");
      window.scrollTo({ top: document.querySelector('[data-testid="form-section"]').offsetTop - 40, behavior: "smooth" });
    } catch (err) {
      setStatus("error");
      setError(err?.response?.data?.detail || "Si è verificato un errore. Riprova.");
    }
  };

  if (status === "success") {
    return (
      <div data-testid="form-success" className="bg-white/5 border border-bordeaux/40 rounded-2xl p-10 text-center">
        <CheckCircle2 className="text-green-400 mx-auto mb-4" size={56} />
        <h3 className="font-display uppercase text-3xl">Candidatura inviata!</h3>
        <p className="text-white/75 mt-3 max-w-md mx-auto">
          Grazie {form.nome.split(" ")[0]}! La tua iscrizione è stata registrata. Riceverai la convocazione all'audizione via email dopo il 20 agosto 2026.
        </p>
        <button onClick={() => { setForm(empty); setStatus("idle"); }} className="mt-6 text-bordeaux font-bold underline">
          Invia un'altra candidatura
        </button>
      </div>
    );
  }

  const input = "w-full bg-white/5 border border-white/15 focus:border-bordeaux focus:ring-2 focus:ring-bordeaux/40 outline-none rounded-xl px-4 py-3 text-white placeholder-white/40 transition";
  const label = "block text-sm font-semibold text-white/80 mb-1.5";

  return (
    <form onSubmit={submit} className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 space-y-5" data-testid="registration-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={label}>Nome e cognome *</label>
          <input className={input} required value={form.nome} onChange={set("nome")} placeholder="Es. Giulia Rossi" data-testid="input-nome" />
        </div>
        <div>
          <label className={label}>Email *</label>
          <input type="email" className={input} required value={form.email} onChange={set("email")} placeholder="nome@email.it" data-testid="input-email" />
        </div>
        <div>
          <label className={label}>Telefono *</label>
          <input className={input} required value={form.telefono} onChange={set("telefono")} placeholder="+39 ..." data-testid="input-telefono" />
        </div>
        <div>
          <label className={label}>Data di nascita * <span className="text-white/40 font-normal">(10–20 anni)</span></label>
          <input type="date" className={input} required value={form.data_nascita} onChange={set("data_nascita")} data-testid="input-data-nascita" />
        </div>
        <div>
          <label className={label}>Città *</label>
          <input className={input} required value={form.citta} onChange={set("citta")} placeholder="Es. Rovigo" data-testid="input-citta" />
        </div>
        <div>
          <label className={label}>Famiglia strumentale *</label>
          <select className={input} required value={form.famiglia} onChange={set("famiglia")} data-testid="select-famiglia">
            <option value="" disabled>Seleziona…</option>
            {families.map((f) => <option key={f} value={f} className="text-black">{f}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Strumento *</label>
          <input className={input} required value={form.strumento} onChange={set("strumento")} placeholder="Es. Violino, Sax contralto…" data-testid="input-strumento" />
        </div>
        <div>
          <label className={label}>Anni di studio *</label>
          <input className={input} required value={form.anni_studio} onChange={set("anni_studio")} placeholder="Es. 3 anni" data-testid="input-anni" />
        </div>
      </div>

      <div>
        <label className={label}>Dove studi? *</label>
        <select className={input} required value={form.contesto} onChange={set("contesto")} data-testid="select-contesto">
          <option value="" disabled>Seleziona…</option>
          {CONTESTI.map((c) => <option key={c} value={c} className="text-black">{c}</option>)}
        </select>
      </div>

      <div>
        <label className={label}>Note (facoltativo)</label>
        <textarea rows={3} className={input} value={form.note} onChange={set("note")} placeholder="Repertorio, esperienze, disponibilità…" data-testid="input-note" />
      </div>

      <label className="flex items-start gap-3 text-sm text-white/75 cursor-pointer">
        <input type="checkbox" checked={form.consenso} onChange={set("consenso")} className="mt-1 w-5 h-5 accent-[#A8123E]" data-testid="checkbox-consenso" />
        <span>Acconsento al trattamento dei miei dati personali per le finalità legate alle selezioni della Trinity Young Orchestra. *</span>
      </label>

      {error && (
        <div className="flex items-center gap-2 text-red-300 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-sm" data-testid="form-error">
          <AlertCircle size={18} /> {error}
        </div>
      )}

      <button type="submit" disabled={status === "loading"} data-testid="submit-button"
        className="w-full bg-bordeaux hover:bg-[#c8164b] disabled:opacity-60 transition-colors text-white font-extrabold text-lg px-6 py-4 rounded-full flex items-center justify-center gap-2">
        {status === "loading" ? <><Loader2 className="animate-spin" size={20} /> Invio in corso…</> : "Invia la candidatura"}
      </button>
    </form>
  );
}
